import { FactList } from "@/shared/components/FactList";
import type { CredentialRequestRecord } from "@/shared/domain/tokens";
import {
  Banner,
  Field,
  RobotoButton,
  TextInput,
} from "@/shared/foundation/primitives";
import { pluralize } from "@/shared/text";

import styles from "./CredentialRequestApproval.module.css";
import { CredentialRequestCard } from "./CredentialRequestCard";

export interface CredentialRequestReviewProps {
  credentialRequest: CredentialRequestRecord;
  signedInAs: string | undefined;
  onApprove: () => void;
  onDeny: () => void;
  isDeciding: boolean;
  errorMessage?: string;
}

/** Show the confirmation code and requesting CLI before the user grants access to their account. */
export function CredentialRequestReview({
  credentialRequest,
  signedInAs,
  onApprove,
  onDeny,
  isDeciding,
  errorMessage,
}: CredentialRequestReviewProps) {
  return (
    <CredentialRequestCard title="Approve CLI sign-in">
      <Field.Root>
        <Field.Label>Confirmation code</Field.Label>
        <Field.Description>
          Check that your terminal shows the same code.
        </Field.Description>
        <Field.Control
          render={<TextInput size="lg" className={styles.confirmationCode} />}
          value={credentialRequest.confirmation_code}
          readOnly
        />
      </Field.Root>

      <FactList>
        {signedInAs !== undefined && (
          <FactList.Item label="Account">{signedInAs}</FactList.Item>
        )}
        <FactList.Item label="Computer">
          {credentialRequest.client_hostname}
        </FactList.Item>
        {credentialRequest.client_ip !== null && (
          <FactList.Item label="Sent from IP address">
            {credentialRequest.client_ip}
          </FactList.Item>
        )}
        <FactList.Item label="CLI version">
          {credentialRequest.client_version}
        </FactList.Item>
        <FactList.Item label="Access token">
          Valid for {credentialRequest.token_expiry_days}{" "}
          {pluralize("day", credentialRequest.token_expiry_days)}
        </FactList.Item>
      </FactList>

      <Banner variant="warning">
        Approve only if you started this sign-in yourself and your terminal
        shows the same code. Approving lets the Roboto CLI on the computer above
        create an access token for your account.
      </Banner>

      {errorMessage !== undefined && (
        <Banner variant="error" role="alert">
          {errorMessage}
        </Banner>
      )}

      <div className={styles.actions}>
        <RobotoButton
          variant="secondary"
          onClick={onDeny}
          disabled={isDeciding}
        >
          Deny
        </RobotoButton>
        <RobotoButton onClick={onApprove} loading={isDeciding}>
          Approve
        </RobotoButton>
      </div>
    </CredentialRequestCard>
  );
}
